import React, { useEffect } from "react";
import { Stack, Typography, Box, Tooltip } from "@mui/material";
import { SliderWithInput } from "../../../../shared/ui";
import { useStoreRoom, useStoreSystem } from "../../../../../store";
import shallow from "zustand/shallow";
import { useShelvingStore } from "../../../../../store/useShelvingStore";
import { roomConstants } from "../../../../../constants";
import { getInfoSections } from "./SectionsParamsTab/utils";
import { getWallLineVertices } from "../../../../SceneObjects/ButtonsScene/utils";
import { getCameraPositionToMiddleLine, getTargetPositionToMiddleLine } from "../../../../../store/helpers";
import { calcRoomDepth } from "../../../../../utils";
import { InfoCircle } from "../../../../SVGasComponent";

interface RoomParamsTabProps {
    proMode: boolean;
}

interface MinSizeReasons {
    length: string[];
    height: string[];
    depth: string[];
}

const RoomParamsTab = ({ proMode }: RoomParamsTabProps) => {
    const { setLength, setHeight, setDepth, height, length, depth, moveDecors, cameraDistance, setCameraDistance } =
        useStoreRoom(
            (state) => ({
                setLength: state.setLength,
                setHeight: state.setHeight,
                setDepth: state.setDepth,
                height: state.height,
                length: state.length,
                depth: state.depth,
                moveDecors: state.moveDecors,
                setCameraDistance: state.setCameraDistance,
                cameraDistance: state.cameraDistance,
            }),
            shallow
        );

    const { currentSide, setSystemProperties } = useStoreSystem(
        (state) => ({
            currentSide: state.currentSide,
            setSystemProperties: state.setSystemProperties,
        }),
        shallow
    );

    const { setPropertyShelf, shelf, updateItemsYPositionsAfterResizeRoomHeight } = useShelvingStore(
        (state) => ({
            setPropertyShelf: state.setPropertyShelving,
            shelf: state.shelving,
            updateItemsYPositionsAfterResizeRoomHeight: state.updateItemsYPositionsAfterResizeRoomHeight,
        }),
        shallow
    );

    const setRoomLength = (newLength: number) => {
        const deltaLength = newLength - length;
        setLength(newLength);
        moveDecors([-deltaLength / 2000, 0, 0], ["left"]);
        moveDecors([deltaLength / 2000, 0, 0], ["right"]);
        setPropertyShelf([
            { wallName: "front", property: "length", value: newLength - shelf.front.sideIndent },
            { wallName: "back", property: "length", value: newLength - shelf.back.sideIndent },
        ]);
        if (!proMode) {
            setCameraDistance(calcRoomDepth(newLength, height));
        }
    };

    const setRoomHeight = (newHeight: number) => {
        const deltaHeight = newHeight - height;
        updateItemsYPositionsAfterResizeRoomHeight(deltaHeight / 1000, "all");
        setHeight(newHeight);
        setPropertyShelf([
            { wallName: "front", property: "height", value: newHeight - shelf.front.offsetCeiling },
            { wallName: "left", property: "height", value: newHeight - shelf.left.offsetCeiling },
            { wallName: "back", property: "height", value: newHeight - shelf.back.offsetCeiling },
            { wallName: "right", property: "height", value: newHeight - shelf.right.offsetCeiling },
        ]);
        if (!proMode) {
            setCameraDistance(calcRoomDepth(length, newHeight));
        }
    };

    const setRoomDepth = (newDepth: number) => {
        const deltaDepth = newDepth - depth;
        setDepth(newDepth);
        moveDecors([0, 0, deltaDepth / 1000], ["left", "right", "back"]);
        setPropertyShelf([
            { wallName: "left", property: "length", value: newDepth - shelf.left.sideIndent },
            { wallName: "right", property: "length", value: newDepth - shelf.right.sideIndent },
        ]);
    };

    const getMinRoomSizeWithReasons = () => {
        const frontWallInfoSections = getInfoSections(shelf.front);
        const backWallInfoSections = getInfoSections(shelf.back);
        const leftWallInfoSections = getInfoSections(shelf.left);
        const rightWallInfoSections = getInfoSections(shelf.right);

        // Получаем декоративные элементы из стора комнаты
        const { decors } = useStoreRoom.getState();

        // Вычисляем минимальные размеры с учетом декоров
        let minLengthFromDecors = 0;
        let minHeightFromDecors = 0;
        let minDepthFromDecors = 0;

        decors.forEach((decor) => {
            if (decor.side === "left" || decor.side === "right" || decor.side === "back") {
                return;
            }
            const decorPos = decor.position;
            const decorWidth = decor.width || 0;
            const decorHeight = decor.height || 0;
            const decorDepth = decor.depth || 0;

            const decorMinLength = (Math.abs(decorPos[0]) + decorWidth / 2) * 2 * 1000;
            const decorMinHeight = (decorPos[1] + decorHeight / 2) * 1000;
            const decorMinDepth = (Math.abs(decorPos[2]) + decorDepth / 2) * 1000;

            minLengthFromDecors = Math.round(Math.max(minLengthFromDecors, decorMinLength));
            minHeightFromDecors = Math.round(Math.max(minHeightFromDecors, decorMinHeight));
            minDepthFromDecors = Math.round(Math.max(minDepthFromDecors, decorMinDepth));
        });

        // Собираем причины для каждого измерения
        const reasons: MinSizeReasons = {
            length: [],
            height: [],
            depth: [],
        };

        // Проверяем длину
        const shelfMinLength = Math.max(frontWallInfoSections.length, backWallInfoSections.length);
        if (shelfMinLength > roomConstants.minSize.length) {
            reasons.length.push("полки на передней/задней стене");
        }
        if (minLengthFromDecors > roomConstants.minSize.length) {
            reasons.length.push("декоративные элементы");
        }

        // Проверяем высоту
        const shelfMinHeight = Math.max(
            frontWallInfoSections.maxHeight,
            backWallInfoSections.maxHeight,
            leftWallInfoSections.maxHeight,
            rightWallInfoSections.maxHeight
        );
        if (shelfMinHeight > roomConstants.minSize.height) {
            reasons.height.push("высота полок");
        }
        if (minHeightFromDecors > roomConstants.minSize.height) {
            reasons.height.push("декоративные элементы");
        }

        // Проверяем глубину
        const shelfMinDepth = Math.max(leftWallInfoSections.length, rightWallInfoSections.length);
        if (shelfMinDepth > roomConstants.minSize.depth) {
            reasons.depth.push("полки на боковых стенах");
        }
        if (minDepthFromDecors > roomConstants.minSize.depth) {
            reasons.depth.push("декоративные элементы");
        }

        const minLength = Math.max(shelfMinLength, minLengthFromDecors, roomConstants.minSize.length);

        const minHeight = Math.max(shelfMinHeight, minHeightFromDecors, roomConstants.minSize.height);

        const minDepth = Math.max(shelfMinDepth, minDepthFromDecors, roomConstants.minSize.depth);

        return {
            minLength,
            minHeight,
            minDepth,
            reasons,
        };
    };

    const { minLength, minHeight, minDepth, reasons } = getMinRoomSizeWithReasons();

    // Компонент для отображения причины минимального размера
    const MinSizeExplanation = ({ dimension, reasons: dimensionReasons }: { dimension: string; reasons: string[] }) => {
        if (dimensionReasons.length === 0) return null;
        const title = `Минимальный размер увеличен из-за: ${dimensionReasons.join(", ")}`;
        return (
            <Tooltip title={title} arrow placement="bottom">
                <div>
                    <InfoCircle />
                </div>
            </Tooltip>
        );
    };

    useEffect(() => {
        const lineVertices = getWallLineVertices(currentSide);
        setSystemProperties([
            { name: "cameraPosition", value: getCameraPositionToMiddleLine(lineVertices) },
            { name: "targetCameraPosition", value: getTargetPositionToMiddleLine(lineVertices) },
        ]);
    }, [height, length, depth, cameraDistance]);

    useEffect(() => {
        if (proMode) {
            if (depth > roomConstants.maxSize.depth) {
                setRoomDepth(roomConstants.maxSize.depth);
            }
        }
    }, [proMode]);

    return (
        <Stack sx={{ gap: "8px" }}>
            {/*@ts-ignore*/}
            <Box>
                <SliderWithInput
                    label={"Длина, мм"}
                    min={minLength}
                    max={roomConstants.maxSize.length}
                    initialValue={length}
                    onChange={setRoomLength}
                    child={<MinSizeExplanation dimension="length" reasons={reasons.length} />}
                />{" "}
            </Box>

            <Box>
                <SliderWithInput
                    label={"Высота, мм"}
                    min={minHeight}
                    max={roomConstants.maxSize.height}
                    initialValue={height}
                    onChange={setRoomHeight}
                    child={<MinSizeExplanation dimension="height" reasons={reasons.height} />}
                />{" "}
            </Box>

            {proMode && (
                <Box>
                    <SliderWithInput
                        label={"Ширина, мм"}
                        min={minDepth}
                        max={roomConstants.maxSize.depth}
                        initialValue={depth}
                        onChange={setRoomDepth}
                        child={<MinSizeExplanation dimension="depth" reasons={reasons.depth} />}
                    />{" "}
                </Box>
            )}
        </Stack>
    );
};

export default RoomParamsTab;
